import { Fragment } from 'react';
import { Check, Lock } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Sprint-2 DREAM-18 stepper.
 *
 * Lock semantics: any step after the first non-completed step is locked
 * (cannot navigate to it). Active step is highlighted; completed steps
 * show a checkmark.
 *
 * Stateless / presentational — parent owns `activeKey` + `onStepClick`.
 * `onStepClick` is invoked only for steps whose status is NOT 'locked'.
 */

export type StepStatus = 'pending' | 'active' | 'completed' | 'locked';

export interface StepDef {
  key: string;
  label: string;
  status: StepStatus;
}

export interface StepperProps {
  steps: StepDef[];
  /** Click handler. Skipped automatically for locked steps. */
  onStepClick?: (key: string) => void;
  /** 'horizontal' (default) or 'vertical' rail layout. */
  orientation?: 'horizontal' | 'vertical';
}

export function Stepper({ steps, onStepClick, orientation = 'horizontal' }: StepperProps) {
  const handleClick = (s: StepDef) => {
    if (s.status === 'locked') return;
    onStepClick?.(s.key);
  };

  if (orientation === 'vertical') {
    return (
      <ol className="space-y-1.5">
        {steps.map((s, idx) => (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => handleClick(s)}
              disabled={s.status === 'locked'}
              aria-current={s.status === 'active' ? 'step' : undefined}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors',
                s.status === 'active' && 'bg-accent-50 text-accent-800',
                s.status === 'completed' && 'text-ink-secondary hover:bg-surface-muted',
                s.status === 'pending' && 'text-ink-secondary hover:bg-surface-muted',
                s.status === 'locked' && 'cursor-not-allowed text-ink-muted',
              )}
            >
              <StepIcon status={s.status} index={idx} />
              <span className="flex-1 truncate">{s.label}</span>
            </button>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ol className="flex w-full items-center gap-1">
      {steps.map((s, idx) => (
        <Fragment key={s.key}>
          {idx > 0 && (
            <span
              aria-hidden
              className={cn(
                'h-px flex-1',
                s.status === 'completed' || s.status === 'active'
                  ? 'bg-accent-300'
                  : 'bg-surface-border',
              )}
            />
          )}
          <li>
            <button
              type="button"
              onClick={() => handleClick(s)}
              disabled={s.status === 'locked'}
              aria-current={s.status === 'active' ? 'step' : undefined}
              className={cn(
                'group flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                s.status === 'active' && 'text-accent-800',
                s.status === 'completed' && 'text-ink-secondary hover:bg-surface-muted',
                s.status === 'pending' && 'text-ink-muted hover:bg-surface-muted',
                s.status === 'locked' && 'cursor-not-allowed text-ink-muted',
              )}
            >
              <StepIcon status={s.status} index={idx} />
              <span className="hidden md:inline">{s.label}</span>
            </button>
          </li>
        </Fragment>
      ))}
    </ol>
  );
}

function StepIcon({ status, index }: { status: StepStatus; index: number }) {
  const base = 'h-6 w-6 shrink-0 rounded-full grid place-items-center text-[10px] font-bold';
  if (status === 'completed') {
    return (
      <span className={cn(base, 'bg-emerald-500 text-white')}>
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === 'active') {
    return <span className={cn(base, 'bg-accent-600 text-white ring-2 ring-accent-100')}>{index + 1}</span>;
  }
  if (status === 'locked') {
    return (
      <span className={cn(base, 'bg-surface-muted text-ink-muted')}>
        <Lock className="h-3 w-3" />
      </span>
    );
  }
  return <span className={cn(base, 'bg-surface-muted text-ink-secondary')}>{index + 1}</span>;
}

/**
 * Helper: given a list of step keys and which ones are completed, derive
 * the StepStatus for each. The first non-completed key becomes 'active';
 * everything after it is 'locked'.
 */
export function deriveStepStatuses<T extends { key: string; label: string }>(
  steps: readonly T[],
  completedKeys: ReadonlySet<string>,
  activeKey?: string,
): StepDef[] {
  let foundActive = false;
  return steps.map((s) => {
    if (completedKeys.has(s.key)) return { key: s.key, label: s.label, status: 'completed' };
    if (!foundActive) {
      foundActive = true;
      return { key: s.key, label: s.label, status: activeKey === s.key || !activeKey ? 'active' : 'pending' };
    }
    return { key: s.key, label: s.label, status: 'locked' };
  });
}
